/**
 * Response mappers — normalise BFF payloads (which mirror the frozen api.yaml
 * aggregation shapes) into domain types. Mapping lives in the api layer per
 * the layer law; pages/components/stores never see raw DTOs.
 *
 * No status derivation here or anywhere in the SPA (ADR-000 F2) —
 * `derivedStatus` is trusted as delivered and validated against the triad.
 */

import { ApiError, type Deployment, type DeploymentSummary, type InvoicePage, type InvoiceRow, type SyncStatus } from '@domain/types'

const TIN_BRANCH_RE = /^[0-9]+_[0-9]+$/
const TRIAD: readonly SyncStatus[] = ['SYNCED', 'FAILED', 'PENDING']

export function assertObject(value: unknown, what: string): Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    throw new ApiError(502, 'INVALID_RESPONSE', `BFF response for ${what} has an unexpected shape.`)
  }
  return value as Record<string, unknown>
}

export function assertString(value: unknown, what: string): string {
  if (typeof value !== 'string') {
    throw new ApiError(502, 'INVALID_RESPONSE', `BFF field ${what} is missing or not a string.`)
  }
  return value
}

export function assertOptionalString(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

export function assertNumber(value: unknown, what: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new ApiError(502, 'INVALID_RESPONSE', `BFF field ${what} is missing or not a number.`)
  }
  return value
}

function assertTriad(value: unknown): SyncStatus {
  if (typeof value === 'string' && (TRIAD as readonly string[]).includes(value)) {
    return value as SyncStatus
  }
  // A status outside the triad is a contract violation in the data path —
  // surfaced as a real error, never silently rendered (BR-2 honesty).
  throw new ApiError(502, 'INVALID_RESPONSE', 'BFF delivered a status outside the Synced/Failed/Pending triad.')
}

/** GET /api/agg/deployments — fleet enumeration (slice 1: Mount Meru). */
export function mapDeployments(payload: unknown): readonly Deployment[] {
  const root = assertObject(payload, 'deployments')
  const list = root['deployments']
  if (!Array.isArray(list)) {
    throw new ApiError(502, 'INVALID_RESPONSE', 'BFF response for deployments has no deployments array.')
  }
  return list.map((entry) => {
    const item = assertObject(entry, 'a deployment')
    const dbName = assertString(item['dbName'], 'dbName')
    if (!TIN_BRANCH_RE.test(dbName)) {
      throw new ApiError(502, 'INVALID_RESPONSE', `BFF dbName "${dbName}" is not in {TPIN}_{branch} form.`)
    }
    const unreachable = item['unreachable'] === true
    return {
      dbName,
      unreachable,
      unreachableReason: unreachable ? assertOptionalString(item['unreachableReason']) : null,
    } satisfies Deployment
  })
}

/** GET /api/agg/deployments/{tinBranch}/summary — FR-1 counts + freshness (FR-6). */
export function mapSummary(payload: unknown): DeploymentSummary {
  const root = assertObject(payload, 'summary')
  return {
    dbName: assertString(root['dbName'], 'dbName'),
    synced: assertNumber(root['synced'], 'synced'),
    failed: assertNumber(root['failed'], 'failed'),
    pending: assertNumber(root['pending'], 'pending'),
    freshness: assertOptionalString(root['freshness']),
  } satisfies DeploymentSummary
}

/** GET /api/agg/deployments/{tinBranch}/invoices — one cursor page (R9). */
export function mapInvoicePage(payload: unknown): InvoicePage {
  const root = assertObject(payload, 'invoices')
  const list = root['invoices']
  if (!Array.isArray(list)) {
    throw new ApiError(502, 'INVALID_RESPONSE', 'BFF response for invoices has no invoices array.')
  }
  const invoices: InvoiceRow[] = list.map((entry) => {
    const item = assertObject(entry, 'an invoice row')
    return {
      rectNo: assertNumber(item['rectNo'], 'rectNo'),
      tin: assertString(item['tin'], 'tin'),
      bhfId: assertString(item['bhfId'], 'bhfId'),
      createdDate: assertString(item['createdDate'], 'createdDate'),
      submissionTimestamp: assertOptionalString(item['submissionTimestamp']),
      resultCd: assertOptionalString(item['resultCd']),
      lastError: assertOptionalString(item['lastError']),
      retryCount: assertNumber(item['retryCount'], 'retryCount'),
      derivedStatus: assertTriad(item['derivedStatus']),
    } satisfies InvoiceRow
  })
  return {
    tinBranch: assertString(root['tinBranch'], 'tinBranch'),
    invoices,
    nextCursor: assertOptionalString(root['nextCursor']),
    hasMore: root['hasMore'] === true,
  } satisfies InvoicePage
}
